import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import {
  SAPHIR_AUTH,
  SAPHIR_HOST_CHROME,
  SAPHIR_LANGUAGE,
  SAPHIR_TENANT,
  SAPHIR_THEME,
  SAPHIR_USER_DIRECTORY,
} from '@saphir/module-contract';

import { AuthService } from '../../services/auth.service';
import { CompanyContextService } from '../../services/company-context.service';
import { LanguageService } from '../../services/language.service';
import { ThemeService } from '../../services/theme.service';
import { UserDirectoryService } from '../../services/user-directory.service';

/**
 * Everything the shell publishes to the modules it hosts, in one place.
 *
 * `useExisting` throughout: a module reads the very service the shell uses, not
 * a copy of it. Sign out and every module sees it; switch the theme and every
 * module follows.
 *
 * The rule this file exists to make obvious: **a module never provides these
 * tokens.** Providing its own would give it a second theme, a second idea of the
 * language, and a session the shell knows nothing about. The contract's README
 * says so, and there is nothing here for a module to import — the tokens come
 * from `@saphir/module-contract`, the implementations do not.
 */
export function provideModuleContext(): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: SAPHIR_AUTH, useExisting: AuthService },
    { provide: SAPHIR_LANGUAGE, useExisting: LanguageService },
    { provide: SAPHIR_THEME, useExisting: ThemeService },
    { provide: SAPHIR_TENANT, useExisting: CompanyContextService },
    { provide: SAPHIR_USER_DIRECTORY, useExisting: UserDirectoryService },

    // `SAPHIR_API_BASE_URL` is NOT here: the shell has no backend of its own.
    // Each module gets its own backend address on its route
    // (`buildModuleRoutes`), from `environment.api.modules`.

    // The header already names the application, and the sidebar draws every
    // module's navigation — a module with its own frame repeats neither.
    {
      provide: SAPHIR_HOST_CHROME,
      useValue: { rendersApplicationName: true, rendersNavigation: true },
    },
  ]);
}
